import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { msalInstance } from '@/app/modules/auth/authConfig';
import { trackSystemError } from '@/app/shared/utils/analyticsEvents';
import { logout } from '../reducers/authSlice';
import type { RootState } from '../../store';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';

const baseQueryWithToken = fetchBaseQuery({
  baseUrl: import.meta.env.REACT_APP_API_URL,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).auth.token;

    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    return headers;
  },
});

export const baseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const result = await baseQueryWithToken(args, api, extraOptions);

  if (result.error) {
    const { status, data } = result.error;

    trackSystemError('API ERROR:', {
      status_code: status,
      errorDescription: (data as any)?.errors?.[0] ||
                        (data as any)?.message ||
                        JSON.stringify(data) || 
                        'Unknown error',
    });
  }

  if (result.error?.status === 401) {
    const alreadyRedirecting = sessionStorage.getItem('auth_redirect_in_progress');

    if (alreadyRedirecting) {
      return result;
    }

    sessionStorage.setItem('auth_redirect_in_progress', 'true');

    const redirectPath = window.location.search;
    sessionStorage.setItem('postLoginRedirect', redirectPath);
    
    sessionStorage.removeItem('isUserCreated');
    api.dispatch(logout());

    try {
      await msalInstance.logoutRedirect({
        postLogoutRedirectUri: `${window.location.origin}/login${redirectPath}`,
      });
    } catch (error) {
      console.error('MSAL logout failed:', error);

      // Allow future logout attempts if redirect failed
      sessionStorage.removeItem('auth_redirect_in_progress');

      window.location.href = `/login${redirectPath}`;
    }
  }

  return result;
};
